import { InlineLoading, Search } from '@metrika/ui-kit/carbon';
import { styled } from '@metrika/ui-kit/emotion';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ResultsContainer, SearchResults } from './SearchResults';
import { SearchOverlayProps, SearchResult } from './types';
import { useSearchIndex, lastSearchTermState } from './useSearchIndex';

interface VisibilityProps {
   isVisible: boolean;
}

export const Overlay = styled.div<VisibilityProps>`
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background: rgba(0, 0, 0, 0.75);
   display: flex;
   flex-direction: column;
   align-items: center;
   padding-top: 4rem;
   opacity: ${(props) => (props.isVisible ? 1 : 0)};
   visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
   transition:
      opacity 0.5s,
      visibility 0.5s;
   z-index: ${(props) => props.theme.zIndex.absoluteTop + 1};
`;

const SearchContainer = styled.div<VisibilityProps>`
   width: 100%;
   max-width: 800px;
   padding: 0 1rem;
   transform: translateY(${(props) => (props.isVisible ? '0' : '-20px')});
   opacity: ${(props) => (props.isVisible ? 1 : 0)};
   transition:
      transform 0.3s,
      opacity 0.3s;
   transition-delay: ${(props) => (props.isVisible ? '0.1s' : '0s')};

   /* WebKit Scrollbar Styles */
   &::-webkit-scrollbar {
      width: 8px; /* Width of the scrollbar */
   }

   &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.colors.uiBackground}; /* Use uiBackground for track */
      border-radius: 4px;
   }

   &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.borderSubtle}; /* Use borderSubtle for thumb */
      border-radius: 4px;
      border: 2px solid ${({ theme }) => theme.colors.uiBackground}; /* Creates padding around thumb */
   }

   &::-webkit-scrollbar-thumb:hover {
      background-color: ${({ theme }) => theme.colors.borderStrong}; /* Use borderStrong for hover */
   }

   /* Firefox Styles (Limited Styling) */
   scrollbar-width: thin; /* Or 'auto' */
   scrollbar-color: ${({ theme }) => theme.colors.borderSubtle} ${({ theme }) => theme.colors.uiBackground}; /* thumb track */
`;

const doSearch = (dataset: SearchResult[], query: string): Promise<SearchResult[]> => {
   return new Promise((resolve) => {
      setTimeout(() => {
         const words = query.split(' ').filter((word) => word.length > 2);
         const results = dataset.filter((result) =>
            words.every(
               (word) =>
                  result.title.toLowerCase().includes(word.toLowerCase()) ||
                  result.description.toLowerCase().includes(word.toLowerCase()) ||
                  (result.labels && result.labels.some((label) => label.toLowerCase().includes(word.toLowerCase()))),
            ),
         );
         resolve(results);
      }, 0); // Simulate network delay
   });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dumpJsonToFile = (data: unknown, filename: string = 'metrika-index.json') => {
   const jsonString = JSON.stringify(data, null, 2);
   const blob = new Blob([jsonString], { type: 'application/json' });
   const url = URL.createObjectURL(blob);

   const a = document.createElement('a');
   a.href = url;
   a.download = filename;
   document.body.append(a);
   a.click();
   a.remove();
   URL.revokeObjectURL(url);
};

export const SearchOverlay: React.FC<SearchOverlayProps> = ({ isOpen, onClose }) => {
   const lastSearchTerm = useRecoilValue(lastSearchTermState);
   const setLastSearchTerm = useSetRecoilState(lastSearchTermState);

   const [searchTerm, setSearchTerm] = useState(lastSearchTerm);
   const searchTermRef = useRef(lastSearchTerm);

   useEffect(() => {
      return () => {
         setLastSearchTerm(searchTermRef.current || '');
      };
   }, [setLastSearchTerm]);

   const [results, setResults] = useState<SearchResult[]>([]);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const [isLoading, setIsLoading] = useState(false);

   const { data, loading } = useSearchIndex();

   const mountedRef = useRef(true);
   useEffect(() => {
      return () => {
         mountedRef.current = false;
      };
   }, []);

   const handleSearch = useCallback(
      async (value: string) => {
         if (!mountedRef.current) return;
         searchTermRef.current = value;
         setSearchTerm(value);
         if (value.length >= 3 && data) {
            setIsLoading(true);
            try {
               const searchResults = await doSearch(data, value);
               setResults(searchResults);
            } finally {
               setIsLoading(false);
            }
         } else {
            setResults([]);
         }
      },
      [data],
   );

   const timeoutRef = useRef<number | null>(null);

   const handleSearchDebounced = useCallback(
      (value: string) => {
         console.log('handleSearchDebounced', value);
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
         }
         timeoutRef.current = window.setTimeout(() => {
            handleSearch(value);
         }, 200);
      },
      [handleSearch],
   );

   const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            onClose();
         }
      },
      [onClose],
   );

   useEffect(() => {
      if (isOpen) {
         document.addEventListener('keyup', handleKeyDown);
         return () => document.removeEventListener('keyup', handleKeyDown);
      }
   }, [isOpen, handleKeyDown]);

   // Trigger search with last term when opening
   useEffect(() => {
      if (isOpen && lastSearchTerm) {
         handleSearch(lastSearchTerm);
      }
   }, [isOpen, lastSearchTerm, handleSearch]);

   const inputRef = useRef<HTMLInputElement>(null);
   useEffect(() => {
      if (isOpen && !loading) {
         setTimeout(() => inputRef.current?.focus(), 20);
      }
   }, [isOpen, loading]);
   return (
      <>
         <SearchContainer isVisible={isOpen}>
            <Search
               ref={inputRef}
               disabled={loading}
               size={'lg'}
               labelText="Search"
               placeholder="Search the platform..."
               onChange={(e) => handleSearchDebounced((e.target.value || '').trim())}
               defaultValue={lastSearchTerm}
            />
            {loading ? (
               <ResultsContainer>
                  <InlineLoading description="Loading index..." />
               </ResultsContainer>
            ) : (
               <SearchResults results={results} searchTerm={searchTerm} onClose={onClose} />
            )}
         </SearchContainer>
      </>
   );
};
